import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { Avatar, Button, Icon, IconButton, Input, Select, Sheet, SkeletonText, Textarea } from "@astrum/ui";
import { ChannelSearch } from "../../components/ChannelSearch.js";
import { PinnedMessages } from "./PinnedMessages.js";
import { useAuth } from "../../lib/auth.js";
import type { ChannelDetail, NotificationLevel } from "./types.js";

/* ──────────────────────────────────── details panel ──────────────────────────────────── */

interface DetailsPanelProps {
  detail: ChannelDetail | undefined;
  loading: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onChangeNotificationLevel: (level: NotificationLevel) => void;
  onAddMembers: () => void;
  onRemoveMember: (member: { id: string; displayName: string }) => void;
  /** The pinned-messages section, supplied by ChannelSidePanels (which knows the channel). */
  pinned?: ReactNode;
  /** Saves the topic (any member) and/or the description (owner or admin). */
  onSaveAbout: (changes: { topic?: string; description?: string }) => Promise<void>;
  /** Archives or restores the channel. */
  onToggleArchive: () => void;
  /** The viewer leaves the channel. */
  onLeave: () => void;
}

/**
 * Topic and description. The topic is for anyone in the channel; the
 * description only for an owner or admin, so it reads as read-only to the rest.
 */
function AboutSection({
  detail,
  onSave,
}: {
  detail: ChannelDetail;
  onSave: (changes: { topic?: string; description?: string }) => Promise<void>;
}) {
  const { channel } = detail;
  const { viewer } = useAuth();
  const isMember = detail.members.some((m) => m.id === viewer?.id);
  const archived = !!channel.archivedAt;
  const canEditTopic = isMember && !archived;
  const canEditDescription = !!channel.canManage && !archived;
  const [editing, setEditing] = useState(false);
  const [topic, setTopic] = useState(channel.topic ?? "");
  const [description, setDescription] = useState(channel.description ?? "");
  const [saving, setSaving] = useState(false);

  // Someone else may change these while this is open to read.
  useEffect(() => {
    if (!editing) {
      setTopic(channel.topic ?? "");
      setDescription(channel.description ?? "");
    }
  }, [channel.topic, channel.description, editing]);

  const save = async () => {
    const changes: { topic?: string; description?: string } = {};
    if (topic.trim() !== (channel.topic ?? "")) changes.topic = topic.trim();
    if (canEditDescription && description.trim() !== (channel.description ?? "")) changes.description = description.trim();
    setSaving(true);
    try {
      if (Object.keys(changes).length) await onSave(changes);
      setEditing(false);
    } catch {
      // The caller has already toasted; stay open so the edit is not lost.
    } finally {
      setSaving(false);
    }
  };

  if (!channel.topic && !channel.description && !canEditTopic) return null;

  return (
    <div className="chat-details__section">
      <div className="chat-details__section-head">
        <h4>About</h4>
        {(canEditTopic || canEditDescription) && !editing && (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>
      {editing ? (
        <form
          className="chat-edit"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Input label="Topic" value={topic} maxLength={200} disabled={!canEditTopic} onChange={(e) => setTopic(e.target.value)} />
          {canEditDescription && (
            <Textarea
              label="Description"
              hint="What this channel is for. Only owners and admins can change it."
              value={description}
              maxRows={6}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
          <div className="chat-edit__actions">
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={saving} disabled={saving}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <>
          {channel.topic && <p className="chat-details__topic">{channel.topic}</p>}
          {channel.description && <p className="chat-details__description">{channel.description}</p>}
          {!channel.topic && !channel.description && <p className="chat-details__hint">No topic yet.</p>}
        </>
      )}
    </div>
  );
}

function DetailsPanelContent({
  detail,
  loading,
  onEdit,
  onDelete,
  onChangeNotificationLevel,
  onAddMembers,
  onRemoveMember,
  pinned,
  onSaveAbout,
  onToggleArchive,
  onLeave,
}: DetailsPanelProps) {
  const { viewer } = useAuth();
  if (loading) {
    return (
      <div className="chat-details__section">
        <SkeletonText lines={4} />
      </div>
    );
  }
  if (!detail) return null;

  return (
    <>
      {detail.channel.kind !== "group" && <AboutSection detail={detail} onSave={onSaveAbout} />}

      {pinned}

      <div className="chat-details__section">
        <div className="chat-details__section-head">
          <h4>Members · {detail.members.length}</h4>
          {/* A direct thread is two named people; adding a third would turn
              somebody's private conversation into a group without asking. */}
          {(detail.channel.canAddMembers ?? detail.channel.canManage) &&
            detail.channel.kind !== "direct" &&
            !detail.channel.archivedAt && (
            <Button size="sm" variant="secondary" onClick={onAddMembers}>
              Add people
            </Button>
          )}
        </div>

        {/*
          * Posting requires membership even in a public channel, so a channel
          * with one member is one nobody else can reply in. Said plainly here
          * rather than left to be discovered by a colleague who cannot type.
          */}
        {detail.channel.kind !== "direct" && detail.channel.kind !== "group" && detail.members.length === 1 && (
          <p className="chat-details__note">
            Only you can post here until you add people — reading a channel and
            replying in it are different things.
          </p>
        )}

        <div className="chat-details__members">
          {detail.members.map((member) => (
            <div key={member.id} className="chat-details__member">
              <Avatar name={member.displayName} src={member.avatarUrl ?? undefined} size={28} />
              <div className="chat-details__member-text">
                <strong>{member.displayName}</strong>
                <span>{member.department ?? member.role}</span>
              </div>
              {/* The creator stays: they are the only non-administrator who
                  can manage the channel, so removing them strands it. */}
              {detail.channel.canManage &&
                detail.channel.kind !== "direct" &&
                detail.channel.kind !== "group" &&
                member.id !== detail.channel.createdById && (
                  <IconButton
                    icon={<Icon name="close" size={13} />}
                    label={`Remove ${member.displayName}`}
                    size="sm"
                    onClick={() => onRemoveMember(member)}
                  />
                )}
            </div>
          ))}
        </div>
      </div>

      {detail.channel.projectId && (
        <div className="chat-details__section">
          <h4>Linked project</h4>
          {/* A Link, not window.location: a full reload would drop the
              socket and re-fetch every channel. */}
          <Link to={`/work/project/${detail.channel.projectId}`} className="chat-details__project-link">
            <Icon name="work" size={14} />
            Open project
          </Link>
        </div>
      )}

      {/* Per-person, so muting a busy channel never mutes it for anyone else.
          Offered on every channel including DMs — the reason to turn a
          conversation down does not depend on how many people are in it. */}
      <div className="chat-details__section">
        <h4>Notifications</h4>
        <Select
          label="Notify me about"
          value={detail.channel.notificationLevel ?? "all"}
          onChange={(e) => onChangeNotificationLevel(e.target.value as NotificationLevel)}
        >
          <option value="all">Every message</option>
          <option value="mentions">Only when I am mentioned</option>
          <option value="none">Nothing</option>
        </Select>
        <p className="chat-details__hint">
          Unread counts still appear in the sidebar whichever you choose.
        </p>
      </div>

      {/* Only the creator or an administrator; a DM has no name or topic to
          edit and is not something you delete out from under the other person. */}
      {detail.members.some((m) => m.id === viewer?.id) &&
        (detail.channel.kind === "group" || detail.channel.createdById !== viewer?.id) && (
          <div className="chat-details__section">
            <Button variant="secondary" size="sm" onClick={onLeave}>
              Leave {detail.channel.kind === "group" ? "group" : "channel"}
            </Button>
          </div>
        )}

      {detail.channel.canManage && detail.channel.kind !== "direct" && detail.channel.kind !== "group" && (
        <div className="chat-details__section">
          <h4>Manage</h4>
          <div className="chat-details__manage">
            <Button variant="secondary" size="sm" icon={<Icon name="edit" size={14} />} onClick={onEdit}>
              Edit channel
            </Button>
            <Button variant="secondary" size="sm" icon={<Icon name="lock" size={14} />} onClick={onToggleArchive}>
              {detail.channel.archivedAt ? "Restore channel" : "Archive channel"}
            </Button>
            <Button variant="danger" size="sm" icon={<Icon name="trash" size={14} />} onClick={onDelete}>
              Delete channel
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

interface ChannelSidePanelsProps extends Omit<DetailsPanelProps, "pinned"> {
  channelId: string;
  searchOpen: boolean;
  onCloseSearch: () => void;
  /** A search result was picked: scroll the thread to it. */
  onJump: (messageId: string) => void;
  detailsOpen: boolean;
  onCloseDetails: () => void;
}

/**
 * The column beside the thread: in-channel search, or the channel's details.
 * Desktop: inline panel. Mobile: a Sheet, opened by the same button.
 */
export function ChannelSidePanels({
  channelId,
  searchOpen,
  onCloseSearch,
  onJump,
  detailsOpen,
  onCloseDetails,
  ...rest
}: ChannelSidePanelsProps) {
  const details = { ...rest, pinned: <PinnedMessages channelId={channelId} onJump={onJump} /> };
  return (
    <>
      {searchOpen && (
        <aside className="chat-search-panel" aria-label="Search this conversation">
          <ChannelSearch channelId={channelId} onClose={onCloseSearch} onJump={onJump} />
        </aside>
      )}

      <aside className={`chat-details ${detailsOpen ? "is-open" : ""}`} aria-label="Channel details">
        <div className="chat-details__header">
          <h3>Details</h3>
          <IconButton icon={<Icon name="close" size={15} />} label="Close details" onClick={onCloseDetails} />
        </div>
        <div className="chat-details__scroll">
          <DetailsPanelContent {...details} />
        </div>
      </aside>

      <div className="chat-details--mobile">
        <Sheet open={detailsOpen} onClose={onCloseDetails} title="Details">
          <DetailsPanelContent {...details} />
        </Sheet>
      </div>
    </>
  );
}
